import { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { useAuth } from '../../context/AuthContext';
import { getRole, getRoleProgress, getSubscriptionSlots, activateRole, getReadinessScore } from '../../services/userApi';
import { useSEO } from '../../hooks/useSEO';
import PageMeta from '../../components/common/PageMeta';
import { Helmet } from 'react-helmet-async';

// ── SVG Icons ──────────────────────────────────────────────────────────────────
function Svg({ children, size = 18, color, sw = '1.75' }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none"
      stroke={color || 'currentColor'} strokeWidth={sw} strokeLinecap="round" strokeLinejoin="round">
      {children}
    </svg>
  );
}
const ArrowLeft   = ({ size = 16 }) => <Svg size={size}><line x1="19" y1="12" x2="5" y2="12"/><polyline points="12 19 5 12 12 5"/></Svg>;
const ChevronDown = ({ size = 18, open }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
    style={{ transform: open ? 'rotate(180deg)' : 'none', transition: 'transform 0.25s ease' }}>
    <polyline points="6 9 12 15 18 9"/>
  </svg>
);
const CheckCircle = ({ size = 16 }) => <Svg size={size} color="#059669"><path d="M22 11.08V12a10 10 0 11-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/></Svg>;
const Lock        = ({ size = 14 }) => <Svg size={size} sw="2"><rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 0110 0v4"/></Svg>;
const Play        = ({ size = 12 }) => <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor"><polygon points="5 3 19 12 5 21 5 3"/></svg>;
const Retry       = ({ size = 12 }) => <Svg size={size} sw="2.5"><polyline points="1 4 1 10 7 10"/><path d="M3.51 15a9 9 0 102.13-9.36L1 10"/></Svg>;
const QuestionMark = () => (
  <svg width={16} height={16} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10"/>
    <path d="M9.09 9a3 3 0 015.83 1c0 2-3 3-3 3"/>
    <line x1="12" y1="17" x2="12.01" y2="17"/>
  </svg>
);
const AiIcon = () => (
  <svg width={16} height={16} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
    <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/>
  </svg>
);
const ShareIcon = () => (
  <svg width={15} height={15} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="18" cy="5" r="3"/><circle cx="6" cy="12" r="3"/><circle cx="18" cy="19" r="3"/>
    <line x1="8.59" y1="13.51" x2="15.42" y2="17.49"/><line x1="15.41" y1="6.51" x2="8.59" y2="10.49"/>
  </svg>
);

const PERF = (pct) => {
  if (pct >= 85) return { label: 'Excellent', color: '#059669', bg: '#D1FAE5' };
  if (pct >= 65) return { label: 'Good',      color: '#2563EB', bg: '#EFF6FF' };
  if (pct >= 45) return { label: 'Fair',       color: '#D97706', bg: '#FEF3C7' };
  return { label: 'Needs work',               color: '#C1272D', bg: '#FEE2E2' };
};

const CHAPTER_COLORS = [
  '#1B3A5C','#C1272D','#7C3AED','#2563EB','#059669',
  '#D97706','#C1272D','#0d2238','#2A5280','#64748B',
];

// ── Confirm choose role modal ──────────────────────────────────────────────────
function ConfirmRoleModal({ role, slotInfo, onConfirm, onCancel, loading }) {
  const FONT       = "'Inter', -apple-system, BlinkMacSystemFont, sans-serif";
  const isJobSeeker = slotInfo?.planType === 'job_seeker';
  const used        = slotInfo?.rolesUsed ?? 0;
  const limit       = slotInfo?.roleLimit ?? 5;
  const afterUsed   = used + 1;

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.6)', zIndex: 10000,
      display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20, backdropFilter: 'blur(4px)', fontFamily: FONT }}
      onClick={e => { if (e.target === e.currentTarget && !loading) onCancel(); }}>
      <div style={{ background: '#fff', borderRadius: 20, padding: '28px 28px 24px', maxWidth: 420, width: '100%',
        boxShadow: '0 24px 64px rgba(0,0,0,0.2)' }}>
        <div style={{ width: 46, height: 46, borderRadius: 14, background: '#EFF6FF', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 18 }}>
          <svg width={22} height={22} viewBox="0 0 24 24" fill="none" stroke="#1B3A5C" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/>
            <path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/>
          </svg>
        </div>
        <p style={{ margin: '0 0 4px', fontSize: 11, fontWeight: 700, color: '#94A3B8', textTransform: 'uppercase', letterSpacing: '0.1em' }}>Choose role</p>
        <h3 style={{ margin: '0 0 6px', fontSize: 19, fontWeight: 800, color: '#0F172A', lineHeight: 1.25 }}>{role?.title}</h3>
        <p style={{ margin: '0 0 20px', fontSize: 13, color: '#64748B', lineHeight: 1.6 }}>
          This gives you full access to 1,000 role-specific questions with AI feedback across MCQ, Written, and Spoken formats.
        </p>
        {slotInfo && (isJobSeeker ? (
          <div style={{ background: '#D1FAE5', border: '1px solid #A7F3D0', borderRadius: 12, padding: '12px 16px', marginBottom: 20 }}>
            <p style={{ margin: 0, fontSize: 13, fontWeight: 700, color: '#065f46' }}>Unlimited access — Job Seeker plan</p>
            <p style={{ margin: '3px 0 0', fontSize: 12, color: '#047857' }}>All roles are included in your subscription.</p>
          </div>
        ) : (
          <div style={{ background: '#F0F6FF', border: '1px solid #DBEAFE', borderRadius: 12, padding: '14px 16px', marginBottom: 20 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
              <p style={{ margin: 0, fontSize: 13, fontWeight: 700, color: '#1B3A5C' }}>Role slots</p>
              <span style={{ fontSize: 13, fontWeight: 800, color: '#1B3A5C' }}>{afterUsed} / {limit}</span>
            </div>
            <div style={{ height: 6, background: '#DBEAFE', borderRadius: 99, overflow: 'hidden', marginBottom: 8 }}>
              <div style={{ height: '100%', width: `${Math.min(100, (afterUsed / limit) * 100)}%`,
                background: afterUsed >= limit ? '#C1272D' : '#1B3A5C', borderRadius: 99 }} />
            </div>
            <p style={{ margin: 0, fontSize: 12, color: '#64748B' }}>
              {afterUsed >= limit ? 'Last slot — upgrade to Job Seeker for unlimited.' : `Using 1 of your ${limit} Full Access role slots.`}
            </p>
          </div>
        ))}
        <div style={{ display: 'flex', gap: 10 }}>
          <button onClick={onCancel} disabled={loading}
            style={{ flex: '0 0 90px', padding: '12px 16px', background: '#F8FAFC', border: '1.5px solid #E2E8F0',
              borderRadius: 12, fontSize: 14, fontWeight: 600, cursor: loading ? 'not-allowed' : 'pointer', color: '#334155', fontFamily: FONT }}>
            Cancel
          </button>
          <button onClick={onConfirm} disabled={loading}
            style={{ flex: 1, padding: '12px 20px', background: loading ? '#94A3B8' : '#1B3A5C',
              border: 'none', borderRadius: 12, fontSize: 14, fontWeight: 700,
              cursor: loading ? 'not-allowed' : 'pointer', color: '#fff', fontFamily: FONT, transition: 'background 0.15s' }}
            onMouseEnter={e => { if (!loading) e.currentTarget.style.background = '#0F2A48'; }}
            onMouseLeave={e => { if (!loading) e.currentTarget.style.background = loading ? '#94A3B8' : '#1B3A5C'; }}>
            {loading ? 'Adding…' : 'Choose this role'}
          </button>
        </div>
      </div>
    </div>
  );
}

function SkillRadarChart({ data }) {
  const N = data.length;
  if (N < 3) return null;
  const cx = 110, cy = 110, R = 78;
  const angle = (i) => (i / N) * 2 * Math.PI - Math.PI / 2;
  const toXY = (i, frac) => [cx + R * frac * Math.cos(angle(i)), cy + R * frac * Math.sin(angle(i))];
  const toStr = ([x, y]) => `${x.toFixed(1)},${y.toFixed(1)}`;
  const gridPoly = (frac) => data.map((_, i) => toStr(toXY(i, frac))).join(' ');
  const dataPoly = data.map((d, i) => toStr(toXY(i, d.score / 100))).join(' ');
  return (
    <svg width={220} height={220} viewBox="0 0 220 220" style={{ display: 'block', overflow: 'visible' }}>
      {[0.25, 0.5, 0.75, 1].map(frac => (
        <polygon key={frac} points={gridPoly(frac)}
          fill={frac === 1 ? 'rgba(27,58,92,0.02)' : 'none'}
          stroke={frac === 1 ? '#CBD5E1' : '#E2E8F0'} strokeWidth={frac === 1 ? 1.5 : 1} />
      ))}
      {data.map((_, i) => {
        const [x2, y2] = toXY(i, 1);
        return <line key={i} x1={cx} y1={cy} x2={x2} y2={y2} stroke="#E2E8F0" strokeWidth={1} />;
      })}
      <polygon points={dataPoly} fill="rgba(27,58,92,0.1)" stroke="#1B3A5C" strokeWidth={2} strokeLinejoin="round" />
      {data.map((d, i) => {
        if (!d.attempted) return null;
        const [dx, dy] = toXY(i, d.score / 100);
        const dotColor = d.score >= 70 ? '#059669' : d.score >= 45 ? '#2563EB' : '#C1272D';
        return <circle key={i} cx={dx} cy={dy} r={4} fill={dotColor} stroke="#fff" strokeWidth={2} />;
      })}
      {data.map((d, i) => {
        const [lx, ly] = toXY(i, 1.32);
        const anchor = Math.abs(lx - cx) < 6 ? 'middle' : lx > cx ? 'start' : 'end';
        const dy2 = ly < cy - 4 ? -4 : ly > cy + 4 ? 12 : 4;
        return (
          <text key={i} x={lx} y={ly + dy2} textAnchor={anchor}
            fontSize={10} fontWeight={d.attempted ? 700 : 400}
            fill={d.attempted ? '#475569' : '#CBD5E1'}
            fontFamily="Inter, -apple-system, sans-serif">{d.chapterNum}</text>
        );
      })}
      {[25, 50, 75].map(pct => {
        const [lx, ly] = toXY(0, pct / 100);
        return <text key={pct} x={lx} y={ly - 4} textAnchor="middle" fontSize={8} fill="#CBD5E1" fontFamily="Inter, sans-serif">{pct}%</text>;
      })}
    </svg>
  );
}

export default function RoleOverviewPage() {
  const { slug } = useParams();
  const { isAuthenticated, hasActiveSubscription, refreshSubscriptions } = useAuth();
  const navigate = useNavigate();

  const [role,     setRole]     = useState(null);
  const [chapters, setChapters] = useState([]);
  const [progress, setProgress] = useState({});
  const [loading,  setLoading]  = useState(true);
  const [expanded, setExpanded] = useState({});
  const [slotInfo,     setSlotInfo]     = useState(null);
  const [confirmOpen,  setConfirmOpen]  = useState(false);
  const [activating,   setActivating]   = useState(false);
  const [shareOpen,    setShareOpen]    = useState(false);
  const [readiness,    setReadiness]    = useState(null);

  useEffect(() => {
    const subRefresh = isAuthenticated ? refreshSubscriptions() : Promise.resolve();
    Promise.all([
      getRole(slug).then(r => { setRole(r.data.role); setChapters(r.data.chapters); }),
      isAuthenticated ? getRoleProgress(slug).then(r => setProgress(r.data)).catch(() => {}) : Promise.resolve(),
      subRefresh,
    ]).catch(() => navigate('/roles')).finally(() => setLoading(false));

    if (isAuthenticated) {
      getRoleProgress(slug).then(r => {
        const rp = r.data;
        if (rp && rp.role_id) {
          getReadinessScore(rp.role_id).then(rs => setReadiness(rs.data)).catch(() => {});
        }
      }).catch(() => {});
      getSubscriptionSlots().then(({ data: d }) => {
        if (d.has_full_access) {
          setSlotInfo({
            isFullAccess: true,
            planType: d.plan_type,
            activatedIds: new Set((d.roles || []).map(r => r.id)),
            roleLimit: d.role_limit,
            rolesUsed: (d.roles || []).length,
          });
        }
      }).catch(() => {});
    }
  }, [slug, isAuthenticated]); // eslint-disable-line

  const handleChooseRole = async () => {
    setActivating(true);
    try {
      const { data } = await activateRole(role.id);
      const s = data.slots;
      setSlotInfo({
        isFullAccess: true,
        planType: s.plan_type,
        activatedIds: new Set((s.roles || []).map(r => r.id)),
        roleLimit: s.role_limit,
        rolesUsed: (s.roles || []).length,
      });
      toast.success(`"${role.title}" added to your profile!`);
      setConfirmOpen(false);
    } catch (err) {
      const e = err.response?.data;
      if (e?.code === 'SLOT_LIMIT_REACHED') {
        toast.error(`All ${slotInfo?.roleLimit} role slots used. Upgrade to Job Seeker for unlimited.`);
      } else {
        toast.error(e?.error || 'Failed to add role. Please try again.');
      }
      setConfirmOpen(false);
    }
    setActivating(false);
  };

  const seoTitle       = role ? `${role.title} Interview Questions: 1,000 Q&A with AI Feedback` : 'CentricQ';
  const seoDescription = role
    ? `Practice ${role.title} interview questions with real-time AI feedback on every answer. 1,000 questions — MCQ, written & spoken. 100 questions free (Chapter 1), no credit card needed.`
    : '';

  const chapterTitles = chapters.map(c => c.title);

  const jsonLd = role ? JSON.stringify([
    {
      '@context': 'https://schema.org',
      '@type': 'Course',
      name: `${role.title} Interview Preparation`,
      description: `Practice ${role.title} interview questions with real-time AI feedback. 1,000 questions across 10 chapters — MCQ, written, and spoken practice.`,
      provider: { '@type': 'Organization', name: 'CentricQ', url: 'https://centricq.com' },
      url: `https://centricq.com/roles/${slug}`,
      educationalLevel: 'Professional',
      about: { '@type': 'Thing', name: `${role.title} job interviews` },
      hasCourseInstance: {
        '@type': 'CourseInstance',
        courseMode: 'Online',
        courseWorkload: 'PT5H',
        instructor: { '@type': 'Organization', name: 'CentricQ AI' },
      },
      offers: [
        { '@type': 'Offer', price: '0', priceCurrency: 'USD', description: '100 questions free (Chapter 1)' },
        { '@type': 'Offer', price: '10.00', priceCurrency: 'USD', priceSpecification: { billingDuration: 'P1M' } },
      ],
    },
    {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Home',         item: 'https://centricq.com' },
        { '@type': 'ListItem', position: 2, name: 'Browse Roles', item: 'https://centricq.com/roles' },
        { '@type': 'ListItem', position: 3, name: `${role.title} Interview Questions`, item: `https://centricq.com/roles/${slug}` },
      ],
    },
    {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: [
        {
          '@type': 'Question',
          name: `What ${role.title} interview questions should I prepare for?`,
          acceptedAnswer: {
            '@type': 'Answer',
            text: `CentricQ covers ${chapterTitles.length} key areas for ${role.title} interviews: ${chapterTitles.join(', ')}. Each area has 100 questions with AI-evaluated feedback.`,
          },
        },
        {
          '@type': 'Question',
          name: `How many ${role.title} interview questions are there?`,
          acceptedAnswer: {
            '@type': 'Answer',
            text: `CentricQ has 1,000 ${role.title} interview questions across 10 chapters, covering multiple choice, written answer, and spoken answer formats. 100 questions are free (full Chapter 1).`,
          },
        },
        {
          '@type': 'Question',
          name: `How do I practice for a ${role.title} interview?`,
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'CentricQ provides 3 answer formats to simulate real interviews: multiple choice for quick knowledge checks, written answers for in-depth responses, and spoken answers to practice verbal delivery. Every answer is evaluated by Claude AI.',
          },
        },
      ],
    },
  ]) : null;

  if (loading) return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#F8FAFC' }}>
      <div style={{ width: 32, height: 32, border: '3px solid #1B3A5C', borderTopColor: 'transparent', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }}/>
      <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
    </div>
  );

  if (!role) return null;

  const hasSubscription = isAuthenticated && hasActiveSubscription(role.id);
  const completedSC     = Object.values(progress).filter(p => p.completed).length;
  const totalSC         = chapters.reduce((s, c) => s + (c.sub_chapters?.length || 0), 0);
  const overallPct      = totalSC > 0 ? Math.round((completedSC / totalSC) * 100) : 0;
  const isChosen        = slotInfo?.isFullAccess && slotInfo.activatedIds?.has(role.id);
  const isJobSeeker     = slotInfo?.planType === 'job_seeker';
  // Full Access/Job Seeker users who haven't explicitly chosen this role yet
  const needsChoose     = slotInfo?.isFullAccess && !isChosen;

  const chapterRadarData = chapters.map(chapter => {
    const scs = chapter.sub_chapters || [];
    const done = scs.filter(sc => progress[sc.id]?.completed);
    if (!done.length) return { title: chapter.title, chapterNum: chapter.chapter_number, score: 0, attempted: false };
    const avg = done.reduce((s, sc) => {
      const p = progress[sc.id];
      return s + Math.round((p.score / p.total_questions) * 100);
    }, 0) / done.length;
    return { title: chapter.title, chapterNum: chapter.chapter_number, score: Math.round(avg), attempted: true };
  });
  const chaptersAttempted = chapterRadarData.filter(d => d.attempted).length;

  return (
    <div style={{ minHeight: '100vh', background: '#F8FAFC', fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif" }}>
      {/* Confirm choose role modal */}
      {confirmOpen && (
        <ConfirmRoleModal
          role={role}
          slotInfo={slotInfo}
          onConfirm={handleChooseRole}
          onCancel={() => { if (!activating) setConfirmOpen(false); }}
          loading={activating}
        />
      )}
      <PageMeta
        title={seoTitle}
        description={seoDescription}
        path={`/roles/${slug}`}
      />
      <Helmet>
        <link rel="preload" as="style" href="https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800&display=swap" />
        <link rel="stylesheet" href="https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800&display=swap" />
      </Helmet>
      {jsonLd && <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: jsonLd }} />}
      <style>{`
        @keyframes spin{to{transform:rotate(360deg)}}
        @keyframes fadeUp{from{opacity:0;transform:translateY(12px)}to{opacity:1;transform:none}}
        .ro-card{animation:fadeUp 0.35s ease both}
        @media(prefers-reduced-motion:reduce){.ro-card{animation:none}}
        @media(max-width:640px){
          .ro-subch-row{padding:10px 16px 10px 16px !important}
          .ro-chapter-header{padding:12px 16px !important}
          .ro-section{padding:0 12px !important}
        }
      `}</style>

      {/* ── Nav ─────────────────────────────────────────────────────────────── */}
      <nav style={{ background: '#0F172A', borderBottom: '1px solid rgba(255,255,255,0.07)', padding: '0 24px', height: 56, display: 'flex', alignItems: 'center', justifyContent: 'space-between', position: 'sticky', top: 0, zIndex: 50 }}>
        <Link to={isAuthenticated ? '/dashboard' : '/roles'}
          style={{ display: 'flex', alignItems: 'center', gap: 8, textDecoration: 'none', color: 'rgba(255,255,255,0.5)', transition: 'color 0.12s', cursor: 'pointer' }}
          onMouseEnter={e => e.currentTarget.style.color='rgba(255,255,255,0.9)'}
          onMouseLeave={e => e.currentTarget.style.color='rgba(255,255,255,0.5)'}>
          <ArrowLeft size={16} />
          <span style={{ fontSize: 14 }}>{isAuthenticated ? 'Dashboard' : 'Browse roles'}</span>
        </Link>
        <Link to="/" style={{ fontSize: 17, fontWeight: 700, color: '#fff', textDecoration: 'none', letterSpacing: '-0.3px' }}>
          Centric<span style={{ color: '#C1272D' }}>Q</span>
        </Link>
        <div style={{ position: 'relative' }}>
          <button
            onClick={() => setShareOpen(o => !o)}
            style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '6px 12px', borderRadius: 8, background: shareOpen ? 'rgba(255,255,255,0.18)' : 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.12)', color: shareOpen ? '#fff' : 'rgba(255,255,255,0.7)', fontSize: 13, fontWeight: 500, cursor: 'pointer', transition: 'all 0.12s', fontFamily: "'Inter', sans-serif" }}
            onMouseEnter={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.15)'; e.currentTarget.style.color = '#fff'; }}
            onMouseLeave={e => { if (!shareOpen) { e.currentTarget.style.background = 'rgba(255,255,255,0.08)'; e.currentTarget.style.color = 'rgba(255,255,255,0.7)'; } }}
          >
            <ShareIcon /> Share
          </button>
          {shareOpen && (() => {
            const shareUrl = `https://centricq.com/roles/${slug}`;
            const shareTitle = role ? `${role.title} Interview Questions — CentricQ` : 'CentricQ Interview Prep';
            const shareText = `Preparing for a ${role?.title || ''} interview? Check out these questions on CentricQ!`;
            const wa = `https://wa.me/?text=${encodeURIComponent(shareText + ' ' + shareUrl)}`;
            const li = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`;
            const tw = `https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(shareUrl)}`;
            const em = `mailto:?subject=${encodeURIComponent(shareTitle)}&body=${encodeURIComponent(shareText + '\n' + shareUrl)}`;
            const opts = [
              { label: 'WhatsApp',   href: wa,   color: '#25D366' },
              { label: 'LinkedIn',   href: li,   color: '#0A66C2' },
              { label: 'Twitter / X',href: tw,   color: '#000000' },
              { label: 'Email',      href: em,   color: '#6B7280' },
            ];
            return (
              <div style={{ position: 'absolute', top: 40, right: 0, zIndex: 200, background: '#fff', border: '1px solid #E2E8F0', borderRadius: 12, boxShadow: '0 8px 24px rgba(0,0,0,0.15)', minWidth: 180, overflow: 'hidden' }}
                onMouseLeave={() => setShareOpen(false)}>
                {opts.map(o => (
                  <a key={o.label} href={o.href} target="_blank" rel="noopener noreferrer"
                    style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '11px 16px', fontSize: 13, fontWeight: 600, color: '#1e293b', textDecoration: 'none', borderBottom: '1px solid #F1F5F9', transition: 'background 0.1s' }}
                    onMouseEnter={e => { e.currentTarget.style.background = '#F8FAFC'; }}
                    onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}>
                    <span style={{ width: 10, height: 10, borderRadius: '50%', background: o.color, display: 'inline-block', flexShrink: 0 }} />
                    {o.label}
                  </a>
                ))}
                <button onClick={async () => { try { await navigator.clipboard.writeText(shareUrl); toast.success('Link copied!'); setShareOpen(false); } catch { toast.error('Could not copy link'); } }}
                  style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '11px 16px', fontSize: 13, fontWeight: 600, color: '#1e293b', background: 'transparent', border: 'none', width: '100%', cursor: 'pointer', fontFamily: 'inherit', transition: 'background 0.1s' }}
                  onMouseEnter={e => { e.currentTarget.style.background = '#F8FAFC'; }}
                  onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}>
                  <span style={{ width: 10, height: 10, borderRadius: '50%', background: '#1B3A5C', display: 'inline-block', flexShrink: 0 }} />
                  Copy link
                </button>
              </div>
            );
          })()}
        </div>
      </nav>

      {/* ── Hero ─────────────────────────────────────────────────────────────── */}
      <div style={{ background: 'linear-gradient(160deg, #0a1929 0%, #0d2238 50%, #1B3A5C 100%)', position: 'relative', overflow: 'hidden' }}>
        <div style={{ position: 'absolute', inset: 0, pointerEvents: 'none', backgroundImage: 'linear-gradient(rgba(255,255,255,0.025) 1px,transparent 1px),linear-gradient(90deg,rgba(255,255,255,0.025) 1px,transparent 1px)', backgroundSize: '56px 56px' }} />
        <div style={{ position: 'absolute', right: 0, top: 0, bottom: 0, width: 384, pointerEvents: 'none', background: 'radial-gradient(ellipse at right center, rgba(193,39,45,0.12) 0%, transparent 65%)' }} />

        <div style={{ position: 'relative', zIndex: 1, maxWidth: 900, margin: '0 auto', padding: '40px 24px 48px' }}>
          {/* ── Breadcrumb nav ─────────────────────────────────────────────── */}
          <nav aria-label="Breadcrumb" style={{ marginBottom: 20 }}>
            <ol style={{ listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '4px 6px' }}>
              <li>
                <Link to="/" style={{ fontSize: 13, color: 'rgba(255,255,255,0.68)', textDecoration: 'none', transition: 'color 0.12s' }}
                  onMouseEnter={e => e.currentTarget.style.color='#fff'}
                  onMouseLeave={e => e.currentTarget.style.color='rgba(255,255,255,0.68)'}>
                  Home
                </Link>
              </li>
              <li style={{ fontSize: 13, color: 'rgba(255,255,255,0.45)' }}>›</li>
              <li>
                <Link to="/roles" style={{ fontSize: 13, color: 'rgba(255,255,255,0.68)', textDecoration: 'none', transition: 'color 0.12s' }}
                  onMouseEnter={e => e.currentTarget.style.color='#fff'}
                  onMouseLeave={e => e.currentTarget.style.color='rgba(255,255,255,0.68)'}>
                  Browse Roles
                </Link>
              </li>
              <li style={{ fontSize: 13, color: 'rgba(255,255,255,0.45)' }}>›</li>
              <li>
                <span style={{ fontSize: 13, color: 'rgba(255,255,255,0.7)', fontWeight: 500 }}>
                  {role.title} Interview Questions
                </span>
              </li>
            </ol>
          </nav>

          {/* Category pill */}
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8, borderRadius: 999, padding: '6px 14px', marginBottom: 16, background: 'rgba(255,255,255,0.1)', border: '1px solid rgba(255,255,255,0.14)' }}>
            <span style={{ width: 6, height: 6, borderRadius: '50%', background: '#C1272D', flexShrink: 0 }} />
            <span style={{ fontSize: 12, fontWeight: 600, color: 'rgba(255,255,255,0.7)' }}>{role.category}</span>
          </div>

          <h1 style={{ margin: '0 0 12px', fontSize: 40, fontWeight: 800, color: '#fff', letterSpacing: '-0.8px', lineHeight: 1.1 }}>
            {role.title} Interview Questions
          </h1>

          {role.description && (
            <p style={{ margin: '0 0 20px', fontSize: 15, lineHeight: 1.75, maxWidth: 580, color: 'rgba(255,255,255,0.6)' }}>
              {role.description}
            </p>
          )}

          {/* Meta stats */}
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, marginBottom: 24 }}>
            {[
              { icon: <QuestionMark />, label: '1,000 questions' },
              { icon: null,             label: '10 chapters'     },
              { icon: <AiIcon />,       label: 'AI feedback',   accent: true },
            ].map(({ icon, label, accent }) => (
              <span key={label} style={{
                display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 13, fontWeight: 500, padding: '6px 14px', borderRadius: 999,
                background: accent ? 'rgba(193,39,45,0.2)' : 'rgba(255,255,255,0.08)',
                border: `1px solid ${accent ? 'rgba(193,39,45,0.3)' : 'rgba(255,255,255,0.12)'}`,
                color: 'rgba(255,255,255,0.75)',
              }}>
                {icon}{label}
              </span>
            ))}
          </div>

          {/* Progress bar — shown if user has started */}
          {isAuthenticated && completedSC > 0 && (
            <div>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8, maxWidth: 380 }}>
                <span style={{ fontSize: 13, fontWeight: 600, color: 'rgba(255,255,255,0.75)' }}>
                  You've completed {overallPct}% of this role
                </span>
                <span style={{ fontSize: 12, color: 'rgba(255,255,255,0.5)' }}>
                  {completedSC}/{totalSC}
                </span>
              </div>
              <div style={{ maxWidth: 380, height: 8, borderRadius: 999, overflow: 'hidden', background: 'rgba(255,255,255,0.12)' }}>
                <div style={{ height: '100%', borderRadius: 999, transition: 'width 0.7s ease', width: `${overallPct}%`, background: '#C1272D' }} />
              </div>
              {totalSC - completedSC > 0 && (
                <p style={{ margin: '8px 0 0', fontSize: 12, color: 'rgba(255,255,255,0.45)' }}>
                  Only {totalSC - completedSC} sub-chapter{totalSC - completedSC !== 1 ? 's' : ''} left to earn your certificate!
                </p>
              )}
            </div>
          )}

          {/* Readiness ring — shown once we have a score */}
          {isAuthenticated && readiness && readiness.readiness_score != null && (() => {
            const score = Math.round(readiness.readiness_score);
            const ringColor = score >= 80 ? '#22C55E' : score >= 60 ? '#3B82F6' : score >= 40 ? '#F59E0B' : '#C1272D';
            const r = 28, circ = 2 * Math.PI * r;
            const dash = (score / 100) * circ;
            return (
              <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginTop: 14,
                background: 'rgba(255,255,255,0.08)', borderRadius: 12, padding: '10px 16px',
                maxWidth: 320, backdropFilter: 'blur(4px)' }}>
                <svg width={68} height={68} viewBox="0 0 68 68" style={{ flexShrink: 0 }}>
                  <circle cx="34" cy="34" r={r} fill="none" stroke="rgba(255,255,255,0.15)" strokeWidth="6"/>
                  <circle cx="34" cy="34" r={r} fill="none" stroke={ringColor} strokeWidth="6"
                    strokeDasharray={`${dash} ${circ - dash}`} strokeDashoffset={circ * 0.25}
                    strokeLinecap="round" style={{ transition: 'stroke-dasharray 0.8s ease' }}/>
                  <text x="34" y="37" textAnchor="middle" fontSize="14" fontWeight="800"
                    fill="#fff" fontFamily="Inter, sans-serif">{score}%</text>
                </svg>
                <div>
                  <p style={{ margin: 0, fontSize: 12, fontWeight: 700, color: 'rgba(255,255,255,0.6)', textTransform: 'uppercase', letterSpacing: '0.08em' }}>Readiness score</p>
                  {score >= 80 ? (
                    <p style={{ margin: '2px 0 0', fontSize: 13, fontWeight: 800, color: '#22C55E' }}>Interview Ready ✓</p>
                  ) : (
                    <p style={{ margin: '2px 0 0', fontSize: 13, color: 'rgba(255,255,255,0.75)' }}>Keep going — {80 - score}% to go</p>
                  )}
                  {readiness.avg_score != null && (
                    <p style={{ margin: '2px 0 0', fontSize: 12, color: 'rgba(255,255,255,0.45)' }}>Avg answer score: {Math.round(readiness.avg_score)}%</p>
                  )}
                </div>
              </div>
            );
          })()}
        </div>
      </div>

      {/* ── Subscribe / Choose Role banner ───────────────────────────────────── */}
      {!hasSubscription ? (
        isAuthenticated ? (
          /* Logged-in free user — show what they have + clear upgrade CTA */
          <div style={{ background: '#FFFBEB', padding: '14px 24px', borderBottom: '1px solid #FDE68A', boxShadow: '0 1px 4px rgba(0,0,0,0.04)' }}>
            <div style={{ maxWidth: 900, margin: '0 auto', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 }}>
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
                <svg width={18} height={18} viewBox="0 0 24 24" fill="none" stroke="#D97706" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0, marginTop: 1 }}>
                  <circle cx="12" cy="12" r="10"/><line x1="12" y1="16" x2="12" y2="12"/><line x1="12" y1="8" x2="12.01" y2="8"/>
                </svg>
                <div>
                  <p style={{ margin: '0 0 2px', fontSize: 14, fontWeight: 700, color: '#92400E' }}>
                    You're on the free plan — Chapter 1 is unlocked
                  </p>
                  <p style={{ margin: 0, fontSize: 13, color: '#B45309' }}>
                    Chapters 2–10 are locked. Subscribe from $10/month to unlock all 1,000 questions + AI feedback.
                  </p>
                </div>
              </div>
              <Link to={`/subscribe/${slug}`}
                style={{ display: 'inline-flex', alignItems: 'center', gap: 8, background: '#C1272D', color: '#fff', textDecoration: 'none', padding: '10px 20px', borderRadius: 8, fontSize: 14, fontWeight: 700, transition: 'background 0.15s', flexShrink: 0, cursor: 'pointer' }}
                onMouseEnter={e => e.currentTarget.style.background='#A8202B'}
                onMouseLeave={e => e.currentTarget.style.background='#C1272D'}>
                Upgrade — from $10/mo
              </Link>
            </div>
          </div>
        ) : (
          /* Guest / unauthenticated */
          <div style={{ background: '#fff', padding: '16px 24px', borderBottom: '1px solid #E2E8F0', boxShadow: '0 1px 4px rgba(0,0,0,0.04)' }}>
            <div style={{ maxWidth: 900, margin: '0 auto', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 }}>
              <div>
                <p style={{ margin: '0 0 2px', fontSize: 14, fontWeight: 600, color: '#0F172A' }}>
                  Chapter 1 is free — 100 questions, no card required
                </p>
                <p style={{ margin: 0, fontSize: 13, color: '#64748B' }}>
                  Create a free account to start, or subscribe from $10/month to unlock all 10 chapters.
                </p>
              </div>
              <div style={{ display: 'flex', gap: 10, flexShrink: 0 }}>
                <Link to="/register"
                  style={{ display: 'inline-flex', alignItems: 'center', gap: 6, background: '#1B3A5C', color: '#fff', textDecoration: 'none', padding: '10px 18px', borderRadius: 8, fontSize: 14, fontWeight: 700, transition: 'background 0.15s', cursor: 'pointer' }}
                  onMouseEnter={e => e.currentTarget.style.background='#142d47'}
                  onMouseLeave={e => e.currentTarget.style.background='#1B3A5C'}>
                  Start free
                </Link>
                <Link to={`/subscribe/${slug}`}
                  style={{ display: 'inline-flex', alignItems: 'center', gap: 6, background: '#C1272D', color: '#fff', textDecoration: 'none', padding: '10px 18px', borderRadius: 8, fontSize: 14, fontWeight: 700, transition: 'background 0.15s', cursor: 'pointer' }}
                  onMouseEnter={e => e.currentTarget.style.background='#A8202B'}
                  onMouseLeave={e => e.currentTarget.style.background='#C1272D'}>
                  Unlock all — $10/mo
                </Link>
              </div>
            </div>
          </div>
        )
      ) : needsChoose ? (
        /* Full Access / Job Seeker user who hasn't chosen this role yet */
        <div style={{ background: '#EFF6FF', padding: '16px 24px', borderBottom: '2px solid #BFDBFE', boxShadow: '0 1px 4px rgba(0,0,0,0.04)' }}>
          <div style={{ maxWidth: 900, margin: '0 auto', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 }}>
            <div>
              <p style={{ margin: '0 0 2px', fontSize: 14, fontWeight: 700, color: '#1B3A5C' }}>
                Choose this role to unlock Chapters 2–10
              </p>
              <p style={{ margin: 0, fontSize: 13, color: '#3B82F6' }}>
                {isJobSeeker
                  ? 'You have unlimited role access on your Job Seeker plan.'
                  : `Uses 1 of your ${slotInfo?.roleLimit} Full Access role slots. Chapter 1 is always free.`}
              </p>
            </div>
            <button
              onClick={() => setConfirmOpen(true)}
              style={{ display: 'inline-flex', alignItems: 'center', gap: 8, background: '#1B3A5C', color: '#fff', border: 'none', padding: '10px 20px', borderRadius: 8, fontSize: 14, fontWeight: 700, transition: 'background 0.15s', flexShrink: 0, cursor: 'pointer', fontFamily: "'Inter', sans-serif" }}
              onMouseEnter={e => e.currentTarget.style.background='#0F2A48'}
              onMouseLeave={e => e.currentTarget.style.background='#1B3A5C'}>
              + Choose this role
            </button>
          </div>
        </div>
      ) : isChosen ? (
        /* Already chosen */
        <div style={{ background: '#F0FDF4', padding: '12px 24px', borderBottom: '1px solid #BBF7D0' }}>
          <div style={{ maxWidth: 900, margin: '0 auto', display: 'flex', alignItems: 'center', gap: 8 }}>
            <svg width={15} height={15} viewBox="0 0 24 24" fill="none" stroke="#059669" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="20 6 9 17 4 12"/>
            </svg>
            <span style={{ fontSize: 13, fontWeight: 600, color: '#059669' }}>
              Role chosen — all chapters unlocked
            </span>
          </div>
        </div>
      ) : null}

      {/* ── Focus Drill CTA ──────────────────────────────────────────────────── */}
      {hasSubscription && completedSC > 0 && role && (
        <div style={{ maxWidth: 900, margin: '24px auto 0', padding: '0 24px' }}>
          <div style={{ background: '#EFF6FF', border: '1px solid #BFDBFE', borderRadius: 10, padding: '14px 20px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16, flexWrap: 'wrap' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <div style={{ width: 34, height: 34, borderRadius: 8, background: '#DBEAFE', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <svg width={16} height={16} viewBox="0 0 24 24" fill="none" stroke="#2563EB" strokeWidth={2.5} strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="16"/><line x1="8" y1="12" x2="16" y2="12"/>
                </svg>
              </div>
              <div>
                <p style={{ margin: '0 0 2px', fontSize: 14, fontWeight: 700, color: '#1E40AF' }}>Focus Drill available</p>
                <p style={{ margin: 0, fontSize: 13, color: '#3B82F6' }}>Practice your lowest-scoring questions — no AI cost, instant feedback.</p>
              </div>
            </div>
            <Link to={`/drill/${role.id}`}
              style={{ flexShrink: 0, display: 'inline-flex', alignItems: 'center', gap: 6, padding: '9px 18px', background: '#2563EB', color: '#fff', borderRadius: 8, fontWeight: 700, fontSize: 13, textDecoration: 'none', transition: 'background 0.15s' }}
              onMouseEnter={e => e.currentTarget.style.background = '#1D4ED8'}
              onMouseLeave={e => e.currentTarget.style.background = '#2563EB'}>
              Start drill →
            </Link>
          </div>
        </div>
      )}

      {/* ── Skill Coverage Map — moved above chapters so users actually see it ── */}
      {isAuthenticated && completedSC > 0 && (
        <div style={{ maxWidth: 900, margin: '16px auto 0', padding: '0 24px' }}>
          {chaptersAttempted >= 2 ? (
            <div style={{ background: '#fff', borderRadius: 12, border: '1px solid #E2E8F0', padding: '20px 24px', boxShadow: '0 1px 3px rgba(0,0,0,0.06)', display: 'flex', gap: 28, alignItems: 'flex-start', flexWrap: 'wrap' }}>
              <div style={{ flexShrink: 0 }}>
                <p style={{ margin: '0 0 10px', fontSize: 12, fontWeight: 700, color: '#1B3A5C', textTransform: 'uppercase', letterSpacing: '0.08em' }}>Skill Coverage Map</p>
                <SkillRadarChart data={chapterRadarData} />
              </div>
              <div style={{ flex: 1, minWidth: 180 }}>
                <p style={{ margin: '0 0 10px', fontSize: 12, fontWeight: 700, color: '#64748B', textTransform: 'uppercase', letterSpacing: '0.08em' }}>Chapter scores</p>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
                  {chapterRadarData.map((d, i) => (
                    <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <span style={{ width: 18, height: 18, borderRadius: '50%', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 8.5, fontWeight: 800, background: !d.attempted ? '#F1F5F9' : d.score >= 70 ? '#D1FAE5' : d.score >= 45 ? '#DBEAFE' : '#FEE2E2', color: !d.attempted ? '#CBD5E1' : d.score >= 70 ? '#059669' : d.score >= 45 ? '#2563EB' : '#C1272D' }}>
                        {d.chapterNum}
                      </span>
                      <span style={{ fontSize: 12, color: d.attempted ? '#334155' : '#94A3B8', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {d.title.length > 24 ? d.title.slice(0, 23) + '…' : d.title}
                      </span>
                      <span style={{ fontSize: 12, fontWeight: 700, minWidth: 36, textAlign: 'right', flexShrink: 0, color: !d.attempted ? '#CBD5E1' : d.score >= 70 ? '#059669' : d.score >= 45 ? '#2563EB' : '#C1272D' }}>
                        {d.attempted ? `${d.score}%` : '—'}
                      </span>
                    </div>
                  ))}
                </div>
                <p style={{ margin: '10px 0 0', fontSize: 11, color: '#94A3B8' }}>{chaptersAttempted}/{chapterRadarData.length} chapters started</p>
              </div>
            </div>
          ) : (
            <div style={{ background: '#F8FAFC', border: '1px dashed #CBD5E1', borderRadius: 12, padding: '20px 24px', display: 'flex', alignItems: 'center', gap: 20 }}>
              <div style={{ opacity: 0.22, flexShrink: 0 }}>
                <svg width={72} height={72} viewBox="0 0 100 100" fill="none">
                  <polygon points="50,8 92,68 8,68" stroke="#1B3A5C" strokeWidth="2" strokeDasharray="4,3"/>
                  <circle cx="50" cy="50" r="32" stroke="#1B3A5C" strokeWidth="2" strokeDasharray="4,3"/>
                  <circle cx="50" cy="50" r="16" stroke="#1B3A5C" strokeWidth="2" strokeDasharray="4,3"/>
                </svg>
              </div>
              <div>
                <p style={{ margin: '0 0 4px', fontSize: 14, fontWeight: 700, color: '#475569' }}>Skill Coverage Map</p>
                <p style={{ margin: '0 0 12px', fontSize: 13, color: '#94A3B8' }}>Complete 2+ chapters to unlock your personalised skill map</p>
                <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8, background: '#EFF6FF', border: '1px solid #BFDBFE', borderRadius: 99, padding: '5px 12px', fontSize: 11, fontWeight: 700, color: '#1B3A5C' }}>
                  <div style={{ display: 'flex', gap: 4 }}>
                    {[0, 1].map(i => (
                      <div key={i} style={{ width: 8, height: 8, borderRadius: '50%', background: i < chaptersAttempted ? '#1B3A5C' : '#DBEAFE' }} />
                    ))}
                  </div>
                  {chaptersAttempted} / 2 chapters started
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ── Chapter list ─────────────────────────────────────────────────────── */}
      <div style={{ maxWidth: 900, margin: '0 auto', padding: '32px 24px 0' }}>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {chapters.map((chapter, chIdx) => {
            const isOpen           = expanded[chapter.id] !== false;
            const subChapters      = chapter.sub_chapters || [];
            const chapterCompleted = subChapters.filter(sc => progress[sc.id]?.completed).length;
            const chapterPct       = subChapters.length > 0
              ? Math.round((chapterCompleted / subChapters.length) * 100) : 0;
            const accentColor      = CHAPTER_COLORS[chIdx % CHAPTER_COLORS.length];
            const allDone          = chapterCompleted === subChapters.length && subChapters.length > 0;

            // Skill tree gate: check if previous chapter meets unlock_min_score
            let isChapterGateLocked = false;
            if (chapter.unlock_min_score && chIdx > 0) {
              const prevChapter = chapters[chIdx - 1];
              const prevSCs = prevChapter.sub_chapters || [];
              if (prevSCs.length > 0) {
                const scores = prevSCs.map(sc => {
                  const p = progress[sc.id];
                  return p?.completed ? Math.round((p.score / p.total_questions) * 100) : 0;
                });
                const avgScore = scores.reduce((a, b) => a + b, 0) / scores.length;
                isChapterGateLocked = avgScore < chapter.unlock_min_score;
              }
            }

            return (
              <div key={chapter.id} className="ro-card"
                style={{
                  background: isChapterGateLocked ? '#F8FAFC' : '#FFFFFF', borderRadius: 12, overflow: 'hidden',
                  border: `1px solid ${isChapterGateLocked ? '#CBD5E1' : '#E2E8F0'}`,
                  boxShadow: '0 1px 3px rgba(0,0,0,0.06), 0 4px 16px rgba(0,0,0,0.04)',
                  animationDelay: `${chIdx * 40}ms`,
                  opacity: isChapterGateLocked ? 0.75 : 1,
                }}>

                {/* Skill-tree gate banner */}
                {isChapterGateLocked && (
                  <div style={{ background: '#FFFBEB', borderBottom: '1px solid #FDE68A', padding: '8px 20px', display: 'flex', alignItems: 'center', gap: 8 }}>
                    <svg width={14} height={14} viewBox="0 0 24 24" fill="none" stroke="#D97706" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
                      <rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 0110 0v4"/>
                    </svg>
                    <span style={{ fontSize: 12, color: '#92400E', fontWeight: 600 }}>
                      Score ≥{chapter.unlock_min_score}% on Chapter {chapters[chIdx - 1]?.chapter_number} to unlock
                    </span>
                  </div>
                )}

                {/* Chapter header */}
                <button
                  onClick={() => !isChapterGateLocked && setExpanded(e => ({ ...e, [chapter.id]: !isOpen }))}
                  style={{ width: '100%', display: 'flex', alignItems: 'center', gap: 16, padding: '16px 20px', background: 'transparent', border: 'none', cursor: isChapterGateLocked ? 'default' : 'pointer', textAlign: 'left', transition: 'background 0.12s' }}
                  onMouseEnter={e => { if (!isChapterGateLocked) e.currentTarget.style.background='#F8FAFC'; }}
                  onMouseLeave={e => e.currentTarget.style.background='transparent'}
                >
                  {/* Chapter number badge */}
                  <div style={{ width: 44, height: 44, borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 800, color: '#fff', fontSize: 14, flexShrink: 0, background: isChapterGateLocked ? '#94A3B8' : allDone ? '#059669' : accentColor }}>
                    {isChapterGateLocked
                      ? <svg width={16} height={16} viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 0110 0v4"/></svg>
                      : allDone
                      ? <svg width={16} height={16} viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
                      : chapter.chapter_number}
                  </div>

                  <div style={{ flex: 1, minWidth: 0 }}>
                    <p style={{ margin: '0 0 8px', fontSize: 15, fontWeight: 600, color: isChapterGateLocked ? '#64748B' : '#0F172A' }}>{chapter.title}</p>
                    {!isChapterGateLocked && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                      <div style={{ width: 120, height: 5, borderRadius: 999, overflow: 'hidden', background: '#F1F5F9' }}>
                        <div style={{ height: '100%', borderRadius: 999, transition: 'width 0.7s ease', width: `${chapterPct}%`, background: allDone ? '#059669' : accentColor }} />
                      </div>
                      <span style={{ fontSize: 11, fontWeight: 600, color: '#94A3B8' }}>
                        {chapterCompleted}/{subChapters.length}
                      </span>
                      {allDone && (
                        <span style={{ fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 999, background: '#D1FAE5', color: '#059669' }}>
                          Complete
                        </span>
                      )}
                      {!allDone && chapterCompleted > 0 && (
                        <span style={{ fontSize: 11, fontWeight: 600, color: accentColor }}>
                          {chapterPct}%
                        </span>
                      )}
                    </div>
                    )}
                  </div>

                  {!isChapterGateLocked && <ChevronDown size={18} open={isOpen} />}
                </button>

                {/* Sub-chapters */}
                {isOpen && !isChapterGateLocked && subChapters.length > 0 && (
                  <div style={{ borderTop: '1px solid #E2E8F0' }}>
                    {subChapters.map((sc, scIdx) => {
                      const scp         = progress[sc.id];
                      const isCompleted = scp?.completed;
                      const isPaidChapter = chapter.chapter_number !== 1;
                      const isLocked    = isPaidChapter && !hasSubscription;
                      const isNeedsChoose = isPaidChapter && hasSubscription && needsChoose;
                      const pct         = isCompleted
                        ? Math.round((scp.score / scp.total_questions) * 100) : 0;
                      const perf        = isCompleted ? PERF(pct) : null;

                      return (
                        <div key={sc.id} className="ro-subch-row"
                          style={{
                            display: 'flex', alignItems: 'center', padding: '12px 20px 12px 76px',
                            borderBottom: scIdx < subChapters.length - 1 ? '1px solid #F8FAFC' : 'none',
                            opacity: (isLocked || isNeedsChoose) ? 0.7 : 1,
                            transition: 'background 0.12s',
                          }}
                          onMouseEnter={e => { if (!isLocked && !isNeedsChoose) e.currentTarget.style.background='#F8FAFC'; }}
                          onMouseLeave={e => e.currentTarget.style.background='transparent'}>

                          {/* Status indicator */}
                          <div style={{ flexShrink: 0, marginRight: 14 }}>
                            {isCompleted ? (
                              <CheckCircle size={18} />
                            ) : (isLocked || isNeedsChoose) ? (
                              <div style={{ width: 18, height: 18, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#F1F5F9', color: '#64748B' }}>
                                <Lock size={10} />
                              </div>
                            ) : (
                              <div style={{ width: 18, height: 18, borderRadius: '50%', border: '2px solid #CBD5E1' }} />
                            )}
                          </div>

                          {/* Title + free tag */}
                          <div style={{ flex: 1, minWidth: 0, marginRight: 12 }}>
                            <p style={{ margin: 0, fontSize: 14, fontWeight: 500, color: '#0F172A', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              <span style={{ marginRight: 6, color: '#475569', fontWeight: 600 }}>{chapter.chapter_number}.{sc.sub_chapter_number}</span>
                              {sc.title}
                            </p>
                            {chapter.chapter_number === 1 && !hasSubscription && (
                              <span style={{ fontSize: 10, padding: '2px 6px', borderRadius: 4, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', background: '#D1FAE5', color: '#059669' }}>
                                Free
                              </span>
                            )}
                          </div>

                          {/* Score + action */}
                          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexShrink: 0 }}>
                            {isCompleted && perf && (
                              <span style={{ fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 6, background: perf.bg, color: perf.color }}>
                                {pct}%
                              </span>
                            )}
                            {isLocked ? (
                              <Link
                                to={isAuthenticated ? `/subscribe/${slug}` : `/login`}
                                state={!isAuthenticated ? { from: { pathname: `/subscribe/${slug}` } } : undefined}
                                style={{ fontSize: 13, fontWeight: 600, textDecoration: 'none', color: '#C1272D', cursor: 'pointer' }}
                                onMouseEnter={e => e.currentTarget.style.textDecoration='underline'}
                                onMouseLeave={e => e.currentTarget.style.textDecoration='none'}>
                                {isAuthenticated ? 'Unlock →' : 'Sign in to unlock →'}
                              </Link>
                            ) : isNeedsChoose ? (
                              <button
                                onClick={() => setConfirmOpen(true)}
                                style={{ fontSize: 12, fontWeight: 700, color: '#1B3A5C', background: '#EFF6FF', border: '1px solid #BFDBFE', borderRadius: 7, padding: '5px 12px', cursor: 'pointer', fontFamily: "'Inter', sans-serif" }}
                                onMouseEnter={e => { e.currentTarget.style.background='#1B3A5C'; e.currentTarget.style.color='#fff'; }}
                                onMouseLeave={e => { e.currentTarget.style.background='#EFF6FF'; e.currentTarget.style.color='#1B3A5C'; }}>
                                Choose role →
                              </button>
                            ) : (
                              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                {/* Primary CTA */}
                                <Link
                                  to={isCompleted ? `/quiz/${sc.id}?retake=true` : `/study/${sc.id}`}
                                  style={{
                                    display: 'inline-flex', alignItems: 'center', gap: 6,
                                    padding: '6px 14px', borderRadius: 7, fontSize: 13, fontWeight: 600,
                                    textDecoration: 'none', transition: 'opacity 0.15s',
                                    ...(isCompleted
                                      ? { background: '#F1F5F9', color: '#334155' }
                                      : { background: accentColor, color: '#fff' }),
                                  }}
                                  onMouseEnter={e => e.currentTarget.style.opacity='0.82'}
                                  onMouseLeave={e => e.currentTarget.style.opacity='1'}>
                                  {isCompleted
                                    ? <><Retry size={11} /> Retake</>
                                    : <><Play  size={11} /> Start</>}
                                </Link>
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* ── Public SEO content section ─────────────────────────────────────── */}
      {/* Visible to ALL users (logged-in and public) — crawlable by Google */}
      <div style={{ maxWidth: 900, margin: '0 auto', padding: '40px 24px 56px' }}>
        <div style={{ background: '#fff', borderRadius: 12, border: '1px solid #E2E8F0', padding: '32px 36px', boxShadow: '0 1px 3px rgba(0,0,0,0.06)' }}>
          <h2 style={{ margin: '0 0 12px', fontSize: 22, fontWeight: 700, color: '#0F172A', letterSpacing: '-0.3px' }}>
            About {role.title} Interview Preparation
          </h2>
          <p style={{ margin: '0 0 28px', fontSize: 15, lineHeight: 1.8, color: '#475569' }}>
            The {role.title} role demands a strong mix of technical knowledge and communication skills. Interviewers typically test core domain expertise, problem-solving ability, and how you communicate your reasoning. CentricQ helps you prepare systematically — covering every topic area with 1,000 questions across 10 chapters. You can practice multiple-choice questions for quick recall, written-answer questions to develop in-depth responses, and spoken-answer questions to rehearse your verbal delivery. Every answer is evaluated by Claude AI, giving you a score, specific feedback, and study tips in real time. 100 questions are free (full Chapter 1) with no credit card required.
          </p>

          {chapters.length > 0 && (
            <>
              <h3 style={{ margin: '0 0 14px', fontSize: 16, fontWeight: 600, color: '#0F172A' }}>
                What you&apos;ll cover
              </h3>
              <ul style={{ margin: 0, padding: 0, listStyle: 'none', display: 'flex', flexDirection: 'column', gap: 8 }}>
                {chapters.slice(0, 3).map((chapter, i) => (
                  <li key={chapter.id} style={{ display: 'flex', alignItems: 'flex-start', gap: 10, fontSize: 14, color: '#334155' }}>
                    <span style={{ flexShrink: 0, width: 22, height: 22, borderRadius: 6, background: CHAPTER_COLORS[i % CHAPTER_COLORS.length], display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: 11, color: '#fff', marginTop: 1 }}>
                      {i + 1}
                    </span>
                    <span style={{ lineHeight: 1.6 }}>{chapter.title}</span>
                  </li>
                ))}
              </ul>
              {chapters.length > 3 && (
                <p style={{ margin: '14px 0 0', fontSize: 13, color: '#64748B' }}>
                  + {chapters.length - 3} more chapter{chapters.length - 3 !== 1 ? 's' : ''} inside
                </p>
              )}
            </>
          )}

          {/* Visible FAQ — mirrors JSON-LD for Google to crawl */}
          <div style={{ borderTop: '1px solid #E2E8F0', marginTop: 28, paddingTop: 28 }}>
            <h3 style={{ margin: '0 0 16px', fontSize: 16, fontWeight: 600, color: '#0F172A' }}>
              Frequently asked questions
            </h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
              {[
                {
                  q: `What ${role.title} interview questions should I prepare for?`,
                  a: chapterTitles.length > 0
                    ? `CentricQ covers ${chapterTitles.length} key areas for ${role.title} interviews: ${chapterTitles.join(', ')}. Each area has 100 questions with AI-evaluated feedback.`
                    : `CentricQ covers all key topic areas for ${role.title} interviews. Each area has 100 questions with AI-evaluated feedback.`,
                },
                {
                  q: `How many ${role.title} interview questions are there?`,
                  a: `CentricQ has 1,000 ${role.title} interview questions across 10 chapters, covering multiple choice, written answer, and spoken answer formats. 100 questions are free (full Chapter 1) with no credit card required.`,
                },
                {
                  q: `How do I practice for a ${role.title} interview?`,
                  a: 'CentricQ offers 3 answer formats to simulate real interviews: multiple choice for quick knowledge checks, written answers for in-depth responses, and spoken answers to practise verbal delivery. Every answer is evaluated by Claude AI with a score and detailed feedback.',
                },
              ].map(({ q, a }) => (
                <div key={q}>
                  <p style={{ margin: '0 0 6px', fontSize: 14, fontWeight: 600, color: '#0F172A', lineHeight: 1.5 }}>{q}</p>
                  <p style={{ margin: 0, fontSize: 14, lineHeight: 1.8, color: '#475569' }}>{a}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
